/**
 * OMON-137 Phase 1: Audit Log read-side panel.
 *
 * Renders the chronological feed of `_auditEvent` docs, newest 200
 * first. Each row shows recorded time, action, code display, agent
 * (person who initiated), entity (_practitionerRole that changed),
 * outcome, and an expandable diff of changed fields.
 *
 * Filters + CSV export are deferred to Phase 2 — Ed's framing for v1
 * is "let me visualise the audit trail", and a chronological dump
 * delivers that without forcing decisions on filter UX now.
 */
import { adminAuditLogList } from "../dom";
import { escapeHtml } from "../utils/html";
import {
  loadAuditEvents,
  loadPersonNames,
  type AuditEventRow,
} from "./loaders";

const ACTION_DISPLAY: Record<string, string> = {
  C: "Created",
  R: "Read",
  U: "Updated",
  D: "Deleted",
  E: "Executed",
};

function actionBadge(action: string | null): string {
  if (action === null) {
    return `<span class="action-badge" title="malformed action code">?</span>`;
  }
  return `<span class="action-badge action-${action}" title="FHIR audit-event-action ${action}">${escapeHtml(
    ACTION_DISPLAY[action] ?? action,
  )}</span>`;
}

function outcomeBadge(
  outcome: "success" | "failure" | "unknown",
): string {
  if (outcome === "unknown") {
    return `<span class="outcome-badge" title="outcome code missing">?</span>`;
  }
  return `<span class="outcome-badge outcome-${outcome}">${outcome}</span>`;
}

function formatRecorded(iso: string | null): string {
  if (iso === null) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  // Compact: YYYY-MM-DD HH:MM:SS UTC. Audit log is global so don't
  // localise — operator can mentally adjust if needed.
  return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
}

function renderChanges(
  changes: AuditEventRow["changes"],
): string {
  if (changes.length === 0) return "";
  const rows = changes
    .map((c) => {
      const before = c.before === "" ? "<em>(empty)</em>" : escapeHtml(c.before);
      const after = c.after === "" ? "<em>(empty)</em>" : escapeHtml(c.after);
      return `${escapeHtml(c.field)}: ${before} → ${after}`;
    })
    .join("\n");
  return `<div class="admin-row-detail">${rows}</div>`;
}

function renderRow(row: AuditEventRow): string {
  const agentLabel = row.agentName
    ? escapeHtml(row.agentName)
    : `<code>${escapeHtml(row.agentPersonDocId ?? "unknown")}</code>`;
  const entityLabel = row.entityRoleDocId
    ? `<code>_practitionerRole/${escapeHtml(row.entityRoleDocId)}</code>`
    : `<span title="entity reference missing">—</span>`;
  const codeLine = row.codeDisplay
    ? escapeHtml(row.codeDisplay)
    : row.code
      ? `<code>${escapeHtml(row.code)}</code>`
      : `<em>unknown code</em>`;
  const outcomeText = row.outcomeText
    ? `<div class="admin-row-detail">${escapeHtml(row.outcomeText)}</div>`
    : "";
  return `
    <div class="admin-row">
      <div class="admin-row-main">
        <div class="admin-row-title">${codeLine}</div>
        <div class="admin-row-meta">
          ${actionBadge(row.action)}
          ${outcomeBadge(row.outcome)}
          <span>${escapeHtml(formatRecorded(row.recorded))}</span>
          <span>by ${agentLabel}</span>
          <span>on ${entityLabel}</span>
        </div>
      </div>
      ${outcomeText}
      ${renderChanges(row.changes)}
    </div>
  `;
}

export async function renderAdminAuditLog(): Promise<void> {
  adminAuditLogList.innerHTML = `<div class="admin-empty">Loading…</div>`;
  let rows: AuditEventRow[];
  try {
    rows = await loadAuditEvents(200);
  } catch (err) {
    console.error("loadAuditEvents failed:", err);
    adminAuditLogList.innerHTML = `<div class="admin-empty">Failed to load _auditEvent — check console.</div>`;
    return;
  }

  if (rows.length === 0) {
    adminAuditLogList.innerHTML = `
      <div class="admin-empty">
        No <code>_auditEvent</code> entries yet.<br>
        Events appear here when role assignments are created, changed, or removed.
      </div>`;
    return;
  }

  const personIds = rows
    .map((r) => r.agentPersonDocId)
    .filter((id): id is string => id !== null);
  const names = await loadPersonNames(personIds);
  for (const r of rows) {
    if (r.agentPersonDocId) r.agentName = names.get(r.agentPersonDocId) ?? null;
  }

  adminAuditLogList.innerHTML = rows.map(renderRow).join("");
}
